import { produce } from "immer";

import { EntityTypeName } from "@ds/model/entity-model";
import { normalizeBase } from "@ds/model/helpers";

import { isPlainObject } from "@ds/utils/type-guards/common-guards";

import { coerceContactsToContactArray } from "./contacts-helpers";
import { isLocationArray } from "./model";

/** List responses must normalize every row even when items omit `tenant_id` / `__typename` (strict `isLocation` would fail). */
const isLocationsQueryOutputShape = (obj: unknown): obj is QueryOutput<Locality> =>
  isPlainObject(obj) && Array.isArray((obj as { items?: unknown }).items);

/**
 * Coerce API `contacts` when the key is present.
 * If the key is omitted, return `undefined` so we do not set `contacts: []` — that lets reducers merge with
 * existing store data instead of wiping contacts on partial list rows.
 */
const normalizeLocationContacts = (entity: Locality): LocationContact[] | undefined => {
  if (!Object.prototype.hasOwnProperty.call(entity, "contacts")) {
    return undefined;
  }
  return coerceContactsToContactArray(entity.contacts);
};

type LocalityWithLegacyContacts = Locality & { location_contacts?: unknown };

const normalizer = (entity: Locality): Locality => {
  const withContactsKey = entity as LocalityWithLegacyContacts;
  let entityForContacts: Locality = entity;
  if (
    !Object.prototype.hasOwnProperty.call(withContactsKey, "contacts") &&
    Object.prototype.hasOwnProperty.call(withContactsKey, "location_contacts") &&
    withContactsKey.location_contacts !== undefined
  ) {
    entityForContacts = {
      ...withContactsKey,
      contacts: coerceContactsToContactArray(withContactsKey.location_contacts) as Locality["contacts"],
    };
  }

  const contacts = normalizeLocationContacts(entityForContacts);
  const next = normalizeBase({
    store_id: undefined,
    description: undefined,
    address: undefined,
    time_zone: undefined,
    ...entityForContacts,
    __typename: entityForContacts.__typename ?? EntityTypeName.LOCATION,
  } as Locality);
  if (contacts !== undefined) {
    next.contacts = contacts;
  } else {
    delete next.contacts;
  }
  return next;
};

export const normalize = ((
  entityOrEntities: Locality | Locality[] | QueryOutput<Locality>,
): Locality | Locality[] | QueryOutput<Locality> => {
  if (isLocationsQueryOutputShape(entityOrEntities)) {
    return produce(entityOrEntities, draft => {
      draft.items = entityOrEntities.items.map(item => normalizer(item as Locality));
    });
  }

  return isLocationArray(entityOrEntities) ? entityOrEntities.map(normalizer) : normalizer(entityOrEntities);
}) as {
  (location: Locality): Locality;
  (locations: Locality[]): Locality[];
  (locationsQueryOutput: QueryOutput<Locality>): QueryOutput<Locality>;
};
